import { useCallback } from 'react';

import type { AnalyticsKeyValue } from '$src/utils/analytics/types';

import { posthog } from '$src/utils';

export enum AnalyticsEventName {
  CHECKOUT_FORM_INIT = 'Checkout form init',
  CARD_DETAILS_ENTERED = 'Card details entered',
  CARD_EXPIRATION_CHANGED = 'Card expiration changed',
  CARD_NUMBER_CHANGED = 'Card number changed',
  CARD_CVC_CHANGED = 'Card CVC changed',
  CARD_TOKEN_CREATION_FAILED = 'Card token creation failed',
  CART_SUMMARY_CLOSED = 'Cart summary closed',
  CART_SUMMARY_OPENED = 'Cart summary opened',
  CART_PREPARATION_SUCCESS = 'Cart preparation success',
  CART_PREPARATION_FAILED = 'Cart preparation failed',
  CLICK_AND_COLLECT_STORE_DRAWER_CLOSED = 'Click and collect drawer closed',
  CLICK_AND_COLLECT_STORE_DRAWER_OPENED = 'Click and collect store drawer opened',
  CLICK_AND_COLLECT_STORE_SELECTED = 'Click and collect store selected',
  CREATE_ORDER_FAILED = 'Create order failed',
  CUSTOMER_LOGGED_IN = 'Customer logged in',
  CUSTOMER_LOGGED_OUT = 'Customer logged out',
  LOCALSTORAGE_AUTH_SUCCESS = 'Localstorage auth success',
  DISCOUNT_CODE_SUBMITTED = 'Discount code submitted',
  DISCOUNT_CODE_FAILED = 'Discount code failed',
  DISCOUNT_CODE_SUCCEEDED = 'Discount code succeeded',
  DISCOUNT_CODE_DELETE_SUBMITTED = 'Discount code delete submitted',
  DISCOUNT_CODE_DELETE_SUCCEEDED = 'Discount code delete succeeded',
  DISCOUNT_CODE_DELETE_FAILED = 'Discount code delete failed',
  EMAIL_FILLED = 'Email filled',
  ESTIMATED_SHIPPING_CHECKOUT_FORM_LOAD_FAILED = 'Estimated shipping checkout form load failed',
  FEATURES_USED = 'Features used',
  FINDER_TRIGGERED = 'Finder triggered',
  GIFT_CARD_USED = 'Gift card used',
  LANGUAGE_SELECTED = 'Language selected',
  LEAVE_ON_ERROR = 'Leave on error',
  LOCATION_FOUND = 'location_found',
  LEFT_CHECKOUT_TAB = 'Left checkout tab',
  NETWORK_ERROR_TRIGGERED = 'Network error triggered',
  NO_SHIPPING_METHOD_LOADED = 'No shipping method loaded',
  ORDER_CREATED = 'Order created',
  ORDER_CREATION_EXECUTED = 'Order creation executed',
  ORDER_CREATION_FAILED = 'Order creation failed',
  OTP_ASKED = 'OTP asked',
  OTP_FAILED = 'OTP failed',
  OTP_SUCCEEDED = 'OTP succeeded',
  OTP_DRAWER_OPENED = 'OTP drawer opened',
  OTP_DRAWER_CLOSED = 'OTP drawer closed',
  PAY_BUTTON_CLICKED = 'Pay button clicked',
  PAY_BUTTON_ENABLED = 'Pay button enabled',
  PAY_BUTTON_DISABLED = 'Pay button disabled',
  PAY_BUTTON_SUBMITTED = 'Pay button submitted',
  PICKUP_POINT_DRAWER_CLOSED = 'Pickup point drawer closed',
  PICKUP_POINT_DRAWER_OPENED = 'Pickup point drawer opened',
  PICKUP_POINT_SELECTED = 'Pickup point selected',
  PICKUP_POINT_GET_FAILED = 'Pickup point get failed',
  PICKUP_POINTS_NOT_LOADED = 'Pickup points not loaded',
  PRICE_MISS_MATCH = 'price_miss_match',
  SHIPPING_METHOD_MISMATCH = 'Shipping method mismatch',
  PRODUCT_ADDED_TO_CART_VIA_UPSELL = 'Product added to cart via upsell',
  PRODUCT_REMOVED_FROM_CART_VIA_UPSELL = 'Product removed from cart via upsell',
  RECOMMENDED_DISCOUNTS_FAILED = 'Recommended discounts failed',
  REPEAT_ORDER_TRIGGERED = 'Repeat order triggered',
  RETURNED_TO_CHECKOUT_TAB = 'Returned to checkout tab',
  SELECTED_SHIPPING_METHOD = 'Selected shipping method',
  SHIPPING_ADDRESS_DRAWER_OPENED = 'Shipping address drawer opened',
  SHIPPING_ADDRESS_DRAWER_CLOSED = 'Shipping address drawer closed',
  SHIPPING_ADDRESS_LIST_OPENED = 'Shipping address list opened',
  SHIPPING_ADDRESS_LIST_CLOSED = 'Shipping address list closed',
  SHIPPING_ADDRESS_FILLED = 'Shipping address filled',
  SHIPPING_METHODS_AVAILABLE = 'Shipping methods available',
  SHIPPING_METHODS_DRAWER_OPENED = 'Shipping methods drawer opened',
  SHIPPING_METHODS_DRAWER_CLOSED = 'Shipping methods drawer closed',
  STILL_NOT_REDIRECTED = 'Still not redirected',
  THREE_DS_CHECK_FAILED = '3DS check failed',
  THREE_DS_FRAME_OPENED = '3DS frame opened',
  THREE_DS_FRAME_VALIDATED = '3DS frame validated',
  UPSELL_CAROUSEL_DISPLAYED = 'Upsell carousel displayed',
  FEATURE_FLAG_ERROR = 'Feature flag error',
  COUNTRY_NOT_SUPPORTED_OPENED = 'Country not supported opened',
  COUNTRY_NOT_SUPPORTED_CLOSED = 'Country not supported closed',
  PAYMENT_METHOD_SELECTED = 'Payment method selected',
  EXIT_INTENT_POPUP_SHOWN = 'Exit intent popup shown',
  EXIT_INTENT_REASON_SELECTED = 'Exit intent reason selected',
  EXIT_INTENT_DISMISSED = 'Exit intent dismissed',
  EMPTY_AUTH_RESPONSE = 'Empty auth response',
  MISSING_CART_DATA_OPENED = 'Missing cart data opened',
  LOYALTY_REDEEM_SUCCEEDED = 'Loyalty redeem succeeded',
  LOYALTY_REDEEM_FAILED = 'Loyalty redeem failed',
  LOYALTY_REDEEM_DISCOUNT_AUTO_APPLY_FAILED = 'Loyalty redeem discount auto apply failed',
  ORDER_ERROR_OUT_OF_STOCK = 'Order error out of stock',
  ORDER_ERROR_SHIPPING_UNAVAILABLE = 'Order error shipping unavailable',
}

export const useTrackAnalytics = (): {
  track: (name: AnalyticsEventName, props?: AnalyticsKeyValue) => void;
  identifyUserProperties: (props: AnalyticsKeyValue) => void;
  identifyUser: (userId: string, props?: AnalyticsKeyValue) => void;
  setEventsProperties: (props: AnalyticsKeyValue) => void;
  getPosthogSessionId: () => string | undefined;
  getPosthogSessionUrl: () => string | undefined;
} => {
  const trackEvent = useCallback((name: AnalyticsEventName, props?: AnalyticsKeyValue) => {
    try {
      // Posthog global properties are set in the useGlobalPropertiesTracking hook
      posthog.trackEvent(name, props);
    } catch (error) {
      console.error('Failed to track event', { error });
      return;
    }
  }, []);

  const identifyUserProperties = useCallback((props: AnalyticsKeyValue) => {
    posthog.setUserProperties(props);
  }, []);

  const identifyUser = useCallback((userId: string, props?: AnalyticsKeyValue) => {
    posthog.identifyUser(userId, props);
  }, []);

  const setEventsProperties = useCallback((props: AnalyticsKeyValue) => {
    posthog.setEventsProperties(props);
  }, []);

  const getPosthogSessionUrl = useCallback(() => {
    return posthog.getSessionUrl();
  }, []);

  const getPosthogSessionId = useCallback(() => {
    return posthog.getSessionId();
  }, []);

  return {
    identifyUser,
    track: trackEvent,
    setEventsProperties,
    getPosthogSessionUrl,
    getPosthogSessionId,
    identifyUserProperties,
  };
};

/**
+ * Tracks an analytics event without React context.
+ * ⚠️ WARNING: This function does not enrich events with shop/cart/message context.
+ * Only use this for events where context is not required or is provided in props.
*/
export const trackAnalyticsEventWithoutHook = (name: AnalyticsEventName, props?: AnalyticsKeyValue) => {
  try {
    posthog.trackEvent(name, props);
  } catch (error) {
    console.error('Failed to track without hook', { error });
  }
};
